import React, { useCallback, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Select, Input, Divider, Checkbox, Button, DatePicker, InputNumber } from 'antd';
import dayjs from 'dayjs';
import * as productApi from '@apis/product';
import * as userApi from '@apis/admin-user';
import { ProductModel, ProductStatus } from '@models/product';
import { ageTextFromBirthday } from '@utils/string';
import { useProgramInvoiceEditStore } from '@stores/programInvoiceEditStore';
import { SelectUser } from '@components/product/SelectUser';
import { ChildInfo } from '@models/chat';
import { WEEKDAY_LABEL, ProgramInvoiceSchedule } from '@models/program-invoice';

const { TextArea } = Input;

const getHourOptions = () => Array.from({ length: 15 }, (_, i) => ({ value: i + 7, label: `${i + 7}시` }));
const getMinuteOptions = () => [0, 15, 30, 45].map((m) => ({ value: m, label: `${m}분` }));

const ScheduleEditor: React.FC<{
  value?: ProgramInvoiceSchedule;
  onChange: (schedule: ProgramInvoiceSchedule) => void;
}> = ({ value, onChange }) => {
  const [scheduleType, setScheduleType] = useState(value?.type || 'day');

  useEffect(() => {
    if (value?.type) {
      setScheduleType(value.type);
    }
  }, [value]);

  const handleDateChange = (index: number, date: dayjs.Dayjs | null) => {
    if (!date) return;
    const newDates = [...(value?.dates || [])];
    newDates[index] = date.toDate();
    onChange({ ...value, type: 'day', dates: newDates } as ProgramInvoiceSchedule);
  };

  const addDate = () => {
    const newDates = [...(value?.dates || []), new Date()];
    onChange({ ...value, type: 'day', dates: newDates } as ProgramInvoiceSchedule);
  };

  const removeDate = (index: number) => {
    const newDates = (value?.dates || []).filter((_, i) => i !== index);
    onChange({ ...value, type: 'day', dates: newDates } as ProgramInvoiceSchedule);
  };

  const handleTimeChange = (time: { hour: number; minute: number }) => {
    onChange({ ...value, type: scheduleType, time: time } as ProgramInvoiceSchedule);
  };

  const handleFrequencyChange = (freq: number) => {
    onChange({ ...value, type: 'month', weeklyFrequency: freq } as ProgramInvoiceSchedule);
  };

  return (
    <div>
      <Select value={scheduleType} onChange={setScheduleType} style={{ width: 120, marginBottom: 16 }}>
        <Select.Option value="day">일별</Select.Option>
        <Select.Option value="week">주별</Select.Option>
        <Select.Option value="month">월별</Select.Option>
      </Select>

      {scheduleType === 'day' && (
        <div>
          {(value?.dates || []).map((date, index) => (
            <div key={index} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              <DatePicker
                value={dayjs(date)}
                onChange={(d) => handleDateChange(index, d)}
                showTime
                format="YYYY-MM-DD HH:mm"
              />
              <Button onClick={() => removeDate(index)} danger>
                삭제
              </Button>
            </div>
          ))}
          <Button onClick={addDate} type="dashed" style={{ width: '100%' }}>
            + 일정 추가
          </Button>
        </div>
      )}

      {scheduleType === 'week' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Checkbox.Group
            options={Object.entries(WEEKDAY_LABEL).map(([value, label]) => ({ label, value }))}
            value={value?.weekdays}
            onChange={(checkedValues) =>
              onChange({ ...value, type: 'week', weekdays: checkedValues as string[] } as ProgramInvoiceSchedule)
            }></Checkbox.Group>
          <div style={{ display: 'flex', gap: 8 }}>
            <Select
              placeholder="시간"
              style={{ width: 100 }}
              options={getHourOptions()}
              value={value?.time?.hour}
              onChange={(h) => handleTimeChange({ hour: h, minute: value?.time?.minute || 0 })}
            />
            <Select
              placeholder="분"
              style={{ width: 100 }}
              options={getMinuteOptions()}
              value={value?.time?.minute}
              onChange={(m) => handleTimeChange({ hour: value?.time?.hour || 9, minute: m })}
            />
          </div>
        </div>
      )}

      {scheduleType === 'month' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <InputNumber
            addonBefore="주"
            addonAfter="회"
            min={1}
            max={7}
            value={value?.weeklyFrequency}
            onChange={(freq) => handleFrequencyChange(freq || 1)}
          />
          <div style={{ display: 'flex', gap: 8 }}>
            <Select
              placeholder="시간"
              style={{ width: 100 }}
              options={getHourOptions()}
              value={value?.time?.hour}
              onChange={(h) => handleTimeChange({ hour: h, minute: value?.time?.minute || 0 })}
            />
            <Select
              placeholder="분"
              style={{ width: 100 }}
              options={getMinuteOptions()}
              value={value?.time?.minute}
              onChange={(m) => handleTimeChange({ hour: value?.time?.hour || 9, minute: m })}
            />
          </div>
        </div>
      )}
    </div>
  );
};

const ProgramInvoiceEditView: React.FC = () => {
  const { data, setData, children, setChildren } = useProgramInvoiceEditStore();
  const [manualParticipants, setManualParticipants] = useState<{ name: string; birthYear: string; gender: string }[]>(
    [],
  );

  const partnerProductsQuery = useQuery({
    queryKey: ['partnerProducts', data.seller?.id],
    queryFn: () =>
      productApi.searchProducts({
        size: 100,
        sellerId: data.seller?.id,
        status: ProductStatus.OPEN,
      }),
    enabled: !!data.seller?.id,
  });

  const handleProductChange = useCallback(
    (productId: string) => {
      const product = partnerProductsQuery.data?.data.contents.find((p: ProductModel) => p._id === productId);
      if (product) {
        setData((prev) => ({
          ...prev,
          product: {
            id: productId,
            saleType: product.saleType,
            name: product.title,
            requestAmount: product.fee,
            durations: product.durations,
            applyGroupDiscount: product.applyGroupDiscount,
          },
          amount: product.fee,
          totalSessionCount: 1,
        }));
      }
    },
    [partnerProductsQuery.data, setData],
  );

  const handlePartnerSelect = (user: { _id: string; profile?: { name?: string; phone?: string } }) => {
    setData((prev) => ({ ...prev, seller: { id: user._id, name: user.profile?.name, phone: user.profile?.phone } }));
  };

  const handleUserSelect = async (user: {
    _id: string;
    profile?: { name?: string; phone?: string; children?: ChildInfo[] };
  }) => {
    try {
      const { data: userData } = await userApi.getUser(user._id);
      setData((prev) => ({
        ...prev,
        representative: { id: userData._id, name: userData.profile?.name || '', phone: userData.profile?.phone || '' },
      }));
      setChildren(userData.profile?.children || []);
    } catch (e) {
      console.error('수신자 정보를 불러오는 데 실패했습니다.');
    }
  };

  const handleParticipantCheck = (child: ChildInfo, checked: boolean) => {
    const participant = {
      name: child.name || '',
      birthYear: child.birthday?.substring(0, 4) || '',
      gender: child.gender || '',
    };
    const currentParticipants = data.participants || [];
    const newParticipants = checked
      ? [...currentParticipants, participant]
      : currentParticipants.filter((p) => p.name !== participant.name);
    setData((prev) => ({ ...prev, participants: newParticipants }));
  };

  const addManualParticipant = () => {
    setManualParticipants([...manualParticipants, { name: '', birthYear: '', gender: '' }]);
  };

  const removeManualParticipant = (index: number) => {
    const newManualParticipants = manualParticipants.filter((_, i) => i !== index);
    setManualParticipants(newManualParticipants);
  };

  const handleManualParticipantChange = (index: number, field: 'name' | 'birthYear' | 'gender', value: string) => {
    const newManualParticipants = [...manualParticipants];
    newManualParticipants[index][field] = value;
    setManualParticipants(newManualParticipants);
  };

  useEffect(() => {
    const existingParticipants = data.participants?.filter((p) => children.some((c) => c.name === p.name)) || [];
    const newParticipants = [...existingParticipants, ...manualParticipants.filter((mp) => mp.name)];
    setData((prev) => ({
      ...prev,
      participants: newParticipants,
    }));
  }, [manualParticipants, children, setData]);

  const calculateAmount = (baseAmount: number, sessions: number, discountTarget: number) => {
    const total = baseAmount * sessions;
    const discountRatio = discountTarget / 10;
    return total - total * discountRatio;
  };

  return (
    <div style={{ maxHeight: '70vh', overflowY: 'auto', padding: '0 16px' }}>
      <Divider orientation="left">파트너 선택</Divider>
      <SelectUser onSelect={handlePartnerSelect} selectedUserId={data.seller?.id} />

      <Divider orientation="left">수신자</Divider>
      <SelectUser onSelect={handleUserSelect} selectedUserId={data.representative?.id} />
      <Input
        addonBefore="이름"
        value={data.representative?.name || ''}
        onChange={(e) =>
          setData((prev) => ({
            ...prev,
            representative: {
              ...prev.representative,
              id: prev.representative?.id || '',
              phone: prev.representative?.phone || '',
              name: e.target.value,
            },
          }))
        }
        style={{ marginTop: 8 }}
      />
      <Input
        addonBefore="연락처"
        value={data.representative?.phone || ''}
        onChange={(e) =>
          setData((prev) => ({
            ...prev,
            representative: {
              ...prev.representative,
              id: prev.representative?.id || '',
              name: prev.representative?.name || '',
              phone: e.target.value,
            },
          }))
        }
        style={{ marginTop: 8 }}
      />

      <Divider orientation="left">수강생</Divider>
      {children.map((child) => (
        <Checkbox
          key={child.name}
          onChange={(e) => handleParticipantCheck(child, e.target.checked)}
          checked={data.participants?.some((p) => p.name === child.name)}>
          {child.name} ({ageTextFromBirthday(child.birthday || '')})
        </Checkbox>
      ))}
      {manualParticipants.map((p, index) => (
        <div key={index} style={{ display: 'flex', gap: 8, marginTop: 8 }}>
          <Input
            placeholder="이름"
            value={p.name}
            onChange={(e) => handleManualParticipantChange(index, 'name', e.target.value)}
          />
          <Input
            placeholder="출생년도"
            value={p.birthYear}
            onChange={(e) => handleManualParticipantChange(index, 'birthYear', e.target.value)}
          />
          <Select
            placeholder="성별"
            value={p.gender}
            onChange={(value) => handleManualParticipantChange(index, 'gender', value)}
            style={{ width: 100 }}>
            <Select.Option value="m">남</Select.Option>
            <Select.Option value="f">여</Select.Option>
          </Select>
          <Button onClick={() => removeManualParticipant(index)} danger>
            삭제
          </Button>
        </div>
      ))}
      <Button onClick={addManualParticipant} type="dashed" style={{ marginTop: 8 }}>
        + 수강생 수동 추가
      </Button>

      <Divider orientation="left">상품 선택</Divider>
      <Select
        style={{ width: '100%' }}
        placeholder="상품 선택"
        value={data.product?.id}
        onChange={handleProductChange}
        loading={partnerProductsQuery.isLoading}>
        {partnerProductsQuery.data?.data.contents.map((p: ProductModel) => (
          <Select.Option key={p._id} value={p._id}>
            {p.title}
          </Select.Option>
        ))}
      </Select>

      <Divider orientation="left">결제 정보</Divider>
      <Input.Group compact style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span>상품 가격: {data.product?.requestAmount?.toLocaleString() || 0}원</span>
        <InputNumber
          addonAfter="회"
          min={1}
          value={data.totalSessionCount}
          onChange={(value) =>
            setData((prev) => ({
              ...prev,
              totalSessionCount: value || 1,
              amount: calculateAmount(prev.product?.requestAmount || 0, value || 1, prev.groupDiscountTarget || 0),
            }))
          }
        />
        {data.product?.applyGroupDiscount && (
          <InputNumber
            addonBefore="그룹할인"
            addonAfter="명"
            min={0}
            value={data.groupDiscountTarget}
            onChange={(value) =>
              setData((prev) => ({
                ...prev,
                groupDiscountTarget: value || 0,
                amount: calculateAmount(prev.product?.requestAmount || 0, prev.totalSessionCount || 1, value || 0),
              }))
            }
          />
        )}
      </Input.Group>
      <Input
        addonBefore="결제 금액"
        addonAfter="원"
        value={data.amount}
        onChange={(e) => setData((prev) => ({ ...prev, amount: Number(e.target.value) || 0 }))}
        style={{ marginTop: 8 }}
      />
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
        <span>결제 주기</span>
        <Select
          value={data.type}
          onChange={(value) => setData((prev) => ({ ...prev, type: value as 'one_time' | 'weekly' | 'monthly' }))}
          style={{ width: 120 }}>
          <Select.Option value="one_time">이번만</Select.Option>
          <Select.Option value="weekly">매주</Select.Option>
          <Select.Option value="monthly">매월</Select.Option>
        </Select>
        <span>결제 유효 시간</span>
        <DatePicker
          value={data.validUntil ? dayjs(data.validUntil) : null}
          onChange={(date) => setData((prev) => ({ ...prev, validUntil: date ? date.toDate() : undefined }))}
          showTime
          format="YYYY-MM-DD HH:mm"
          placeholder="설정 안함"
        />
      </div>

      <Divider orientation="left">수강 정보</Divider>
      <Input
        addonBefore="수강증 이름"
        value={data.title}
        onChange={(e) => setData((prev) => ({ ...prev, title: e.target.value }))}
      />
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
        <span>소요 시간</span>
        <InputNumber
          addonAfter="시간"
          min={0}
          value={data.duration?.hour}
          onChange={(value) =>
            setData((prev) => ({
              ...prev,
              duration: {
                hour: value || 0,
                minute: prev.duration?.minute || 0,
                duration: (value || 0) * 60 + (prev.duration?.minute || 0),
              },
            }))
          }
        />
        <InputNumber
          addonAfter="분"
          min={0}
          max={59}
          step={10}
          value={data.duration?.minute}
          onChange={(value) =>
            setData((prev) => ({
              ...prev,
              duration: {
                hour: prev.duration?.hour || 0,
                minute: value || 0,
                duration: (prev.duration?.hour || 0) * 60 + (value || 0),
              },
            }))
          }
        />
      </div>

      <Divider orientation="left">수업 일정</Divider>
      <ScheduleEditor
        value={data.invoiceSchedule}
        onChange={(schedule) => setData((prev) => ({ ...prev, invoiceSchedule: schedule }))}
      />

      <Divider orientation="left">메모</Divider>
      <TextArea
        rows={4}
        value={data.memo}
        onChange={(e) => setData((prev) => ({ ...prev, memo: e.target.value }))}
        placeholder="메모를 입력하세요"
      />
    </div>
  );
};

export default ProgramInvoiceEditView;
